import { Controller } from "@hotwired/stimulus";

// Quote-reply plumbing for thread composers, in two roles that share one
// identifier:
//
// - On the composer (has the preview targets): listens for `message-reply:set`
//   on the document, fills the hidden replyable fields, shows the preview, and
//   focuses the editor.
// - On a message's actions menu (no targets): `reply` reads the replyable
//   from the button's data attributes and dispatches that event.
//
// The event travels through the document because the menu and the composer
// are siblings, not ancestor and descendant. Only the composer role
// subscribes, so a long thread does not register one listener per message.
const EVENT = "message-reply:set";

export default class extends Controller<HTMLElement> {
  static targets = [
    "replyPreview",
    "replyableType",
    "replyableId",
    "replyContent",
  ];

  declare readonly replyPreviewTarget: HTMLDivElement;
  declare readonly replyableTypeTarget: HTMLInputElement;
  declare readonly replyableIdTarget: HTMLInputElement;
  declare readonly replyContentTarget: HTMLDivElement;

  declare readonly hasReplyPreviewTarget: boolean;
  declare readonly hasReplyableTypeTarget: boolean;
  declare readonly hasReplyableIdTarget: boolean;
  declare readonly hasReplyContentTarget: boolean;

  private handleReplyEvent = (event: Event): void => {
    const { replyableType, replyableId, previewContent } = (event as CustomEvent).detail ?? {};
    this.setReply(replyableType ?? "", replyableId ?? "", previewContent ?? "");
  };

  connect(): void {
    if (this.composer) document.addEventListener(EVENT, this.handleReplyEvent);
  }

  disconnect(): void {
    document.removeEventListener(EVENT, this.handleReplyEvent);
  }

  setReply(replyableType: string, replyableId: string, previewContent: string): void {
    if (this.hasReplyableTypeTarget) this.replyableTypeTarget.value = replyableType;
    if (this.hasReplyableIdTarget) this.replyableIdTarget.value = replyableId;
    if (this.hasReplyContentTarget) this.replyContentTarget.innerHTML = previewContent;
    if (this.hasReplyPreviewTarget) this.replyPreviewTarget.classList.remove("hidden");

    this.element
      .querySelector<HTMLElement>('[data-mention-contenteditable-target="editor"]')
      ?.focus();
  }

  clearReply(): void {
    if (this.hasReplyableTypeTarget) this.replyableTypeTarget.value = "";
    if (this.hasReplyableIdTarget) this.replyableIdTarget.value = "";
    if (this.hasReplyContentTarget) this.replyContentTarget.innerHTML = "";
    if (this.hasReplyPreviewTarget) this.replyPreviewTarget.classList.add("hidden");
  }

  // From a message's actions menu.
  reply(event: Event): void {
    const button = event.currentTarget as HTMLElement;

    document.dispatchEvent(
      new CustomEvent(EVENT, {
        detail: {
          replyableType: button.dataset.replyableType || "",
          replyableId: button.dataset.replyableId || "",
          previewContent: button.dataset.replyPreviewContent || "",
        },
      }),
    );
  }

  private get composer(): boolean {
    return this.hasReplyPreviewTarget;
  }
}
